import React from "react";
import MeshField from "./MeshField";
import Doodle, { type DoodleName } from "./Doodle";
import { handwritten } from "../editorial";

// Shared hero for the story-site sub-pages (LL-0199).
//
// The sub-pages all opened the same way: a flat cream band, a Caveat kicker, a
// headline, a lede. Correct and completely inert, and the reason they still
// read as a different website from the home page even after they inherited the
// display face. This gives them the same opening move the home page has, the
// brand mesh plus scattered marks, without each page re-implementing it.
//
// The doodle set is a PROP rather than fixed, so pages differ from each other:
// a page about safety should not carry the same marks as one about communities.
//
// Motion: `.story-enter` (a pure CSS animation defined in styles.css) rather
// than framer-motion, for the reason documented in Reveal.tsx. framer-motion
// writes its initial state as an inline style, and on this prerendered site
// that ships opacity:0 into the static HTML, so the headline cannot paint until
// hydration. These pages currently have that bug: 15 such elements on
// /how-it-works, 11 on /communities, 10 on /safety. Adopting this hero removes
// it from each page's header as a side effect.

type Mark = {
  name: DoodleName;
  size: number;
  rotate?: number;
  className: string;
};

type Props = {
  kicker: string;
  /** Headline. Pass JSX so a page can highlight part of it. */
  title: React.ReactNode;
  lede?: React.ReactNode;
  /** Decorative marks, positioned per page. */
  marks?: Mark[];
  /** Extra content under the lede (quick nav, chips, a CTA). */
  children?: React.ReactNode;
  /** `ambient` sits back for text-heavy pages; `hero` is denser. */
  meshVariant?: "hero" | "ambient";
};

const StoryPageHero: React.FC<Props> = ({
  kicker,
  title,
  lede,
  marks = [],
  children,
  meshVariant = "ambient",
}) => (
  <section className="relative w-full overflow-hidden px-6 md:px-12 pt-36 pb-20">
    <MeshField variant={meshVariant} />

    {marks.length > 0 && (
      <div aria-hidden="true" className="absolute inset-0">
        {marks.map((m, i) => (
          <Doodle
            key={`${m.name}-${i}`}
            name={m.name}
            size={m.size}
            rotate={m.rotate}
            className={`absolute story-float ${m.className}`}
            style={{ animationDelay: `${-1.3 * i}s` }}
          />
        ))}
      </div>
    )}

    <div className="relative max-w-5xl mx-auto">
      <p
        className="story-enter text-3xl md:text-4xl text-salmon-dark mb-3 -rotate-1 origin-left"
        style={handwritten}
      >
        {kicker}
      </p>
      <h1
        className="story-enter font-headline text-ink tracking-[-0.03em] leading-[1.04] text-[clamp(2.6rem,8vw,5.2rem)] text-balance"
        style={
          { fontWeight: 700, "--enter-delay": "120ms" } as React.CSSProperties
        }
      >
        {title}
      </h1>
      {lede && (
        <p
          className="story-enter mt-7 text-lg md:text-2xl text-ink/70 max-w-2xl leading-relaxed"
          style={{ "--enter-delay": "260ms" } as React.CSSProperties}
        >
          {lede}
        </p>
      )}
      {children && (
        <div
          className="story-enter mt-10"
          style={{ "--enter-delay": "380ms" } as React.CSSProperties}
        >
          {children}
        </div>
      )}
    </div>
  </section>
);

export default StoryPageHero;
